import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { useSession } from '../store/session';
import { offlineQueue } from '../offline/queue';
import { signOut } from '../services/auth';
import { PERSONA_LABELS } from '@stem/contracts';

interface NavItem {
  to: string;
  label: string;
  /** Any one of these is enough to show it. */
  permissions?: string[];
}

/**
 * The authenticated shell.
 *
 * Navigation is filtered by the permissions in the org token, which is a
 * courtesy rather than a control — every route behind it is checked again by
 * `RbacGuard`. Filtering matters anyway: a teacher who can see "Members" and
 * gets a 403 every time learns that the app is broken, not that they lack a
 * permission.
 */
export function AppShell() {
  const { orgSlug = '' } = useParams();
  const { profile, activeOrg, orgs, permissions } = useSession();
  const [pending, setPending] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => offlineQueue.onChange(setPending), []);

  const items: NavItem[] = [
    { to: '', label: 'Dashboard' },
    { to: 'courses', label: 'Courses', permissions: ['course@read'] },
    { to: 'labs', label: 'Virtual labs', permissions: ['lab@read'] },
    { to: 'certificates', label: 'Certificates', permissions: ['certificate@read'] },
    { to: 'members', label: 'People', permissions: ['member@read'] },
    { to: 'billing', label: 'Subscription', permissions: ['subscription@read'] },
    { to: 'packs', label: 'Curriculum packs', permissions: ['pack@install'] },
  ];

  const visible = items.filter(
    item =>
      !item.permissions ||
      permissions.includes('*@*') ||
      item.permissions.some(permission => permissions.includes(permission)),
  );

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-20 border-b border-ink-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <Link to={`/o/${orgSlug}`} className="flex items-center gap-2">
            <span aria-hidden="true" className="text-xl">
              🔬
            </span>
            <span className="hidden text-sm font-bold leading-tight text-brand-900 sm:block">
              STEM Africa
              <span className="block text-[0.7rem] font-medium text-ink-500">Learning Hub</span>
            </span>
          </Link>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
            {visible.map(item => (
              <NavLink
                key={item.to}
                to={`/o/${orgSlug}/${item.to}`}
                end={item.to === ''}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium ${
                    isActive ? 'bg-brand-50 text-brand-800' : 'text-ink-600 hover:bg-ink-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {pending > 0 && (
              /*
                Visible, always, when there is anything queued. A learner who
                worked through a power cut needs to know their work is held and
                will be sent — not to wonder whether it was lost.
              */
              <span
                className="badge bg-savanna-400/20 text-savanna-600"
                title="Saved on this device, waiting to sync"
              >
                {pending} to sync
              </span>
            )}

            <Link
              to="/switch"
              className="hidden max-w-[14rem] truncate rounded-lg border border-ink-200 px-3 py-1.5 text-xs text-ink-700 hover:bg-ink-100 sm:block"
              title={`${orgs.length} organisation${orgs.length === 1 ? '' : 's'}`}
            >
              {activeOrg?.name ?? orgSlug}
              <span className="ml-1 text-ink-400">▾</span>
            </Link>

            <button
              type="button"
              className="rounded-full bg-brand-900 px-3 py-1.5 text-xs font-semibold text-white"
              onClick={() => setMenuOpen(open => !open)}
              aria-expanded={menuOpen}
            >
              {initials(profile?.fullName ?? profile?.email ?? '?')}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-ink-200 bg-white">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 text-sm">
              <p className="font-semibold text-ink-900">{profile?.fullName}</p>
              <p className="text-ink-500">{profile?.email}</p>
              {profile && (
                <p className="text-xs text-ink-500">
                  Signed in as {PERSONA_LABELS[profile.persona]} ·{' '}
                  <Link to="/onboarding" className="text-brand-700 underline">
                    change
                  </Link>
                </p>
              )}
              <p className="text-xs text-ink-500">
                Role in {activeOrg?.name}: <strong>{activeOrg?.role}</strong>
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Link to="/switch" className="btn-secondary">
                  Switch organisation
                </Link>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void signOut().then(() => window.location.assign('/login'))}
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}

        <nav className="flex gap-1 overflow-x-auto border-t border-ink-200 px-3 py-2 lg:hidden">
          {visible.map(item => (
            <NavLink
              key={item.to}
              to={`/o/${orgSlug}/${item.to}`}
              end={item.to === ''}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
                  isActive ? 'bg-brand-50 text-brand-800' : 'text-ink-600'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
}
